import type { Card, ProductType } from "@/context/AppContext";

/**
 * 0–100 heuristic that estimates how attractive a card mix is to a buyer.
 * The signal is independent of profit — it measures buyer-side desirability:
 * rarity diversity, theme coherence, condition quality, and whether the
 * product hits a sensible price-per-card sweet spot.
 *
 * Used by the Product Lot Studio to rank opportunities. NOT a price modifier.
 */
export function computeBuyerValueScore(
  cards: Card[],
  productType: ProductType,
): number {
  if (cards.length === 0) return 0;

  let score = 50; // neutral baseline

  // ── Rarity diversity (±15) ──
  const rarities = new Set(cards.map((c) => c.rarity));
  const rarityVariety = rarities.size;
  if (rarityVariety >= 4) score += 15;
  else if (rarityVariety >= 3) score += 10;
  else if (rarityVariety >= 2) score += 5;

  // ── Theme coherence (±10) ──
  const supertypes = new Set(
    cards.map((c) => c.tcgSupertype).filter((s): s is string => !!s),
  );
  const tcgTypes = new Set(
    cards.flatMap((c) => c.tcgTypes ?? []).filter(Boolean),
  );
  const themed: ProductType[] = [
    "Type Theme Bundle",
    "Character Theme Bundle",
    "Set Builder Lot",
    "Trainer Staples Kit",
    "Energy Kit",
  ];
  if (themed.includes(productType)) {
    if (tcgTypes.size === 1) score += 10;
    else if (tcgTypes.size === 2) score += 5;
    else if (tcgTypes.size > 4) score -= 5;
  }
  if (
    productType === "Trainer Staples Kit" &&
    supertypes.size === 1 &&
    supertypes.has("Trainer")
  ) {
    score += 5;
  }
  if (
    productType === "Energy Kit" &&
    supertypes.size === 1 &&
    supertypes.has("Energy")
  ) {
    score += 5;
  }

  // ── Condition quality (±10) ──
  const totalQty = cards.reduce((s, c) => s + (c.quantity ?? 1), 0);
  const damaged = cards
    .filter((c) => c.condition === "Damaged" || c.condition === "HP")
    .reduce((s, c) => s + (c.quantity ?? 1), 0);
  if (totalQty > 0) {
    const damageRatio = damaged / totalQty;
    if (damageRatio === 0) score += 5;
    else if (damageRatio > 0.2) score -= 10;
    else if (damageRatio > 0.1) score -= 5;
  }
  const allNm = cards.every((c) => c.condition === "NM");
  if (allNm) score += 5;

  // ── Duplicate penalty (±5) ──
  // Excessive single-name duplicates make a "bundle" feel like filler. This
  // applies only to Random / Theme / Set bundles — Playsets and Energy Kits
  // are *expected* to have duplicates.
  if (
    productType !== "4x Playset" &&
    productType !== "Energy Kit" &&
    productType !== "Mystery Pack"
  ) {
    const nameCounts = new Map<string, number>();
    for (const c of cards) {
      const key = c.name.toLowerCase();
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + (c.quantity ?? 1));
    }
    const maxDup = Math.max(0, ...nameCounts.values());
    if (maxDup > 6) score -= 5;
  }

  // ── Price-per-card sweet spot (±10) ──
  const market = cards.reduce(
    (s, c) => s + (c.marketPrice ?? 0) * (c.quantity ?? 1),
    0,
  );
  const pricePerCard = totalQty > 0 ? market / totalQty : 0;
  if (
    productType === "100-card Random Lot" ||
    productType === "Set Builder Lot"
  ) {
    if (pricePerCard >= 0.15 && pricePerCard <= 1.5) score += 10;
    else if (pricePerCard < 0.05) score -= 5;
  } else if (productType === "Pre-built Deck" || productType === "Deck Core") {
    if (pricePerCard >= 0.4 && pricePerCard <= 4) score += 10;
  } else if (productType === "Premium Single") {
    if (pricePerCard >= 5) score += 10;
  } else {
    if (pricePerCard >= 0.25 && pricePerCard <= 3) score += 5;
  }

  return clamp(Math.round(score), 0, 100);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
